// Related emails: note the emails that belong to this item (SWI staff only). From V0.5 the pm@ mailbox fills these in.
import { useState } from 'react';
import { write } from '../../lib/api';
import { fmtDateTime } from '../../lib/format';
import { Button, errText, useToast } from '../kit';

export type ItemEmail = {
  id: string;
  subject: string;
  from_addr: string | null;
  to_addr: string | null;
  sent_at: string | null;
  direction: 'in' | 'out';
  web_link: string | null;
  note: string | null;
  deleted_at: string | null;
};

const safeLink = (u: string | null) => (u && /^https:\/\//i.test(u) ? u : null);

export function Emails({ itemId, list, canEdit, onChanged }: { itemId: string; list: ItemEmail[]; canEdit: boolean; onChanged: () => void }) {
  const toast = useToast();
  const [adding, setAdding] = useState(false);
  const [f, setF] = useState({ subject: '', from_addr: '', sent_at: '', web_link: '', note: '', direction: 'in' as 'in' | 'out' });
  const live = list.filter((e) => !e.deleted_at).sort((a, b) => (b.sent_at ?? '').localeCompare(a.sent_at ?? ''));

  async function add() {
    if (!f.subject.trim()) return;
    try {
      await write('item_emails', 'insert', {
        values: {
          item_id: itemId,
          subject: f.subject.trim(),
          from_addr: f.from_addr.trim() || null,
          sent_at: f.sent_at ? new Date(f.sent_at).toISOString() : null,
          web_link: safeLink(f.web_link.trim()),
          note: f.note.trim() || null,
          direction: f.direction,
        },
        summary: `Linked email: ${f.subject.trim().slice(0, 80)}`,
      });
      setF({ subject: '', from_addr: '', sent_at: '', web_link: '', note: '', direction: 'in' });
      setAdding(false);
      onChanged();
    } catch (e) {
      toast({ tone: 'error', text: errText(e) });
    }
  }
  async function remove(e: ItemEmail) {
    if (!window.confirm(`Unlink “${e.subject}”? It can be restored from the audit log.`)) return;
    try {
      await write('item_emails', 'delete', { id: e.id, summary: `Unlinked email: ${e.subject.slice(0, 80)}` });
      onChanged();
    } catch (err) {
      toast({ tone: 'error', text: errText(err) });
    }
  }

  return (
    <div>
      {live.length === 0 && !adding ? <p className="muted small" style={{ margin: '0 0 8px' }}>No emails linked yet. From V0.5, emails copied to pm@ are matched here automatically.</p> : null}
      {live.map((e) => {
        const link = safeLink(e.web_link);
        return (
          <div className="mail" key={e.id}>
            <span className="mail-ico" aria-hidden="true">
              {e.direction === 'out' ? '↗' : '✉'}
            </span>
            <div className="fu-main">
              {link ? (
                <a href={link} target="_blank" rel="noopener noreferrer" className="fu-title">
                  {e.subject}
                </a>
              ) : (
                <span className="fu-title">{e.subject}</span>
              )}
              <span className="muted small">
                {e.direction === 'out' ? 'Sent' : 'From'} {e.from_addr || '—'}
                {e.sent_at ? ` · ${fmtDateTime(e.sent_at)}` : ''}
                {e.note ? ` · ${e.note}` : ''}
              </span>
            </div>
            {canEdit ? (
              <button type="button" className="linkbtn danger small" onClick={() => void remove(e)}>
                Unlink
              </button>
            ) : null}
          </div>
        );
      })}
      {canEdit ? (
        adding ? (
          <div className="mail-form">
            <input value={f.subject} onChange={(e) => setF({ ...f, subject: e.target.value })} placeholder="Subject" aria-label="Subject" autoFocus />
            <input value={f.from_addr} onChange={(e) => setF({ ...f, from_addr: e.target.value })} placeholder="From (name or address)" aria-label="From" />
            <input type="datetime-local" value={f.sent_at} onChange={(e) => setF({ ...f, sent_at: e.target.value })} aria-label="Date and time" />
            <select value={f.direction} onChange={(e) => setF({ ...f, direction: e.target.value as 'in' | 'out' })} aria-label="Direction">
              <option value="in">Received</option>
              <option value="out">Sent by us</option>
            </select>
            <input value={f.web_link} onChange={(e) => setF({ ...f, web_link: e.target.value })} placeholder="Outlook link (optional — Open in browser, copy the address)" aria-label="Link to the email" className="wide" />
            <input value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} placeholder="Note (optional)" aria-label="Note" className="wide" />
            <div className="resp-row">
              <Button kind="primary" onClick={() => void add()} disabled={!f.subject.trim()}>
                Link email
              </Button>
              <Button onClick={() => setAdding(false)}>Cancel</Button>
            </div>
          </div>
        ) : (
          <Button kind="ghost" onClick={() => setAdding(true)}>
            + Link email
          </Button>
        )
      ) : null}
    </div>
  );
}
